// Composer — textarea + attach/mic/send + attached-file tray.
//
// Attachments (Phase 1 polish):
//   - Paperclip → file picker (images only for now)
//   - Paste an image from clipboard → adds it as an attachment
//   - Thumbnails render above the textarea with an × to remove
//
// Text attachments (.md, .pdf, etc.) come later — the old client folds
// extracted text into the userMessage with <attachment> tags; we'll
// port that with the file-picker plumbing when there's a clear ask.

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { MicListeningOverlay } from "../../components/mic-listening-overlay";
import type { Attachment } from "../../lib/use-chat";
import { useMic } from "../../lib/use-mic";
import { api, type Skill } from "../../lib/rpc";

interface ComposerProps {
  draft: string;
  /** Accepts either a string (replace) or a function (reducer-style
   *  update). Functional form is required for the mic chunk-stream
   *  flow so accumulating transcripts don't race against a stale
   *  `draft` closure captured at hook-mount time. */
  setDraft: (next: string | ((prev: string) => string)) => void;
  send: (attachments: Attachment[], explicitText?: string) => void;
  disabled?: boolean;
  notify: (msg: string) => void;
}

// Cap on a single image upload. Larger than this and we'd risk the
// per-RPC JSON body limit on the server (it has a global 32 MiB cap)
// and burn the user's bandwidth on a turn that the LLM doesn't need at
// full resolution anyway.
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;

export function ChatComposer({ draft, setDraft, send, disabled = false, notify }: ComposerProps) {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  // Separate input for audio uploads — `accept="audio/*"` filters the
  // OS picker, so reusing the image-attach `fileRef` would either show
  // the wrong filter or fight over the accept attribute mid-render.
  const audioFileInputRef = useRef<HTMLInputElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  // Skill catalog for the slash menu. Loaded once on mount — the inventory
  // is small (well under 100 entries) so we filter client-side as the user
  // types. Refetched only when the page reloads; if the user authors a new
  // skill mid-session, they'll see it after a refresh, which mirrors how
  // the rest of the read-once panels (presets, memory) behave.
  const [skills, setSkills] = useState<Skill[]>([]);
  useEffect(() => {
    let cancelled = false;
    api.skillsList()
      .then((list) => { if (!cancelled) setSkills(list); })
      .catch(() => { if (!cancelled) setSkills([]); });
    return () => { cancelled = true; };
  }, []);

  // Slash-menu activation: draft starts with `/` and the user hasn't yet
  // typed a space (a space signals "skill name done, now args"). Query is
  // everything after the slash up to first space. Matching is two-tier so
  // typing `recipe` still surfaces `recipe-search`: prefix matches first,
  // then substring matches that aren't already in the prefix list.
  const slashQuery = useMemo(() => {
    if (!draft.startsWith("/")) return null;
    const firstSpace = draft.indexOf(" ");
    return firstSpace === -1 ? draft.slice(1) : null;
  }, [draft]);
  const slashMatches = useMemo<Skill[]>(() => {
    if (slashQuery === null) return [];
    const q = slashQuery.toLowerCase();
    const prefix = skills.filter((s) => s.name.toLowerCase().startsWith(q));
    const contains = skills.filter((s) =>
      !s.name.toLowerCase().startsWith(q) && s.name.toLowerCase().includes(q)
    );
    return [...prefix, ...contains].slice(0, 8);
  }, [skills, slashQuery]);
  const slashOpen = slashMatches.length > 0;
  const [slashIdx, setSlashIdx] = useState(0);
  useEffect(() => { setSlashIdx(0); }, [slashQuery]);

  const acceptSlash = (s: Skill) => {
    // Keep any args the user already typed past the first space — but the
    // menu only opens BEFORE a space, so this branch is essentially "insert
    // `/name ` and let them keep typing".
    const firstSpace = draft.indexOf(" ");
    const tail = firstSpace === -1 ? "" : draft.slice(firstSpace);
    setDraft(`/${s.name}${tail || " "}`);
    // Refocus the textarea — clicking an item blurs it.
    requestAnimationFrame(() => taRef.current?.focus());
  };

  // Mic → STT. As of v0.3.5 the mic flow is TRUE STREAMING via WS.
  // Server emits the FULL running turn transcript on every partial
  // Voice-assistant style as of v0.3.6: while the mic is active we show
  // a full-screen listening overlay instead of flickering streaming
  // partials in the draft. Only onStreamFinal updates the draft, with
  // the same baseline-replacement logic so the user's pre-mic typing
  // is preserved.
  //
  // File uploads (uploadFile) also fire onStreamStart + onStreamFinal
  // with the full transcript — same replacement code path.
  const streamBaselineRef = useRef<string>("");
  const mic = useMic({
    onStreamStart: () => {
      // Capture whatever's in the draft right now via the functional
      // setDraft trick — it gives us the latest committed value
      // without depending on a stale closure.
      setDraft((prev) => {
        streamBaselineRef.current = prev;
        return prev;
      });
    },
    onStreamFinal: (turnText) => {
      const baseline = streamBaselineRef.current;
      const trimmedBase = baseline.replace(/\s+$/, "");
      const fullText = trimmedBase ? `${trimmedBase} ${turnText}` : turnText;
      streamBaselineRef.current = "";
      // Voice-assistant auto-send: dictation commits to the model
      // immediately when the final lands. Empty transcripts (VAD heard
      // nothing) just clear the draft baseline without sending.
      if (turnText.trim() && !disabled) {
        setDraft("");
        send(attachments, fullText);
        setAttachments([]);
      } else {
        setDraft(fullText);
      }
    },
    onError: (msg) => notify(msg),
  });

  // Auto-grow the textarea with its content. Reset to "auto" first so it
  // can also SHRINK when lines are removed, then size to the content.
  // CSS caps it (max-height) and scrolls past that. Runs on every draft
  // change so typing, mic dictation, slash-accept, and the post-send
  // clear all keep the box the right height.
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
  }, [draft]);

  const canSend = !disabled && (draft.trim().length > 0 || attachments.length > 0);

  const onPickFiles = async (filesList: FileList | null | undefined) => {
    if (!filesList || filesList.length === 0) return;
    const next: Attachment[] = [];
    for (const f of Array.from(filesList)) {
      if (!f.type.startsWith("image/")) continue;
      if (f.size > MAX_IMAGE_BYTES) continue;
      const dataUrl = await readAsDataUrl(f);
      next.push({
        kind: "image",
        name: f.name || "image",
        dataUrl,
        mime: f.type,
        size: f.size,
      });
    }
    if (next.length > 0) setAttachments((prev) => [...prev, ...next]);
  };

  // Paste an image from the clipboard while the textarea is focused.
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const imageFiles: File[] = [];
      for (const it of Array.from(items)) {
        if (it.kind === "file") {
          const f = it.getAsFile();
          if (f && f.type.startsWith("image/")) imageFiles.push(f);
        }
      }
      if (imageFiles.length > 0) {
        e.preventDefault();
        const dt = new DataTransfer();
        for (const f of imageFiles) dt.items.add(f);
        onPickFiles(dt.files);
      }
    };
    ta.addEventListener("paste", onPaste);
    return () => ta.removeEventListener("paste", onPaste);
  }, []);

  const doSend = () => {
    if (!canSend) return;
    send(attachments);
    setAttachments([]); // clear after send (the bubble owns them now)
  };

  const removeAt = (i: number) => {
    setAttachments((prev) => prev.filter((_, idx) => idx !== i));
  };

  return (
    <div className="composer-row" data-disabled={disabled}>
      {(mic.state === "streaming" || mic.state === "transcribing") && (
        <MicListeningOverlay
          state={mic.state}
          level={mic.level}
          onStop={() => void mic.toggle()}
          onCancel={() => mic.cancel()}
        />
      )}
      {slashOpen && (
        <div className="slash-menu" role="listbox" aria-label="Skills">
          {slashMatches.map((s, i) => (
            <button
              key={s.name}
              type="button"
              className="slash-item"
              data-active={i === slashIdx}
              onMouseEnter={() => setSlashIdx(i)}
              onMouseDown={(e) => {
                // mousedown over click — click fires after blur, by which
                // point the menu has already vanished. mousedown captures
                // the intent before the textarea loses focus.
                e.preventDefault();
                acceptSlash(s);
              }}
              role="option"
              aria-selected={i === slashIdx}
            >
              <span className="slash-name">/{s.name}</span>
              {s.argumentHint && <span className="slash-hint">{s.argumentHint}</span>}
              {s.description && <span className="slash-desc">{s.description}</span>}
            </button>
          ))}
        </div>
      )}
      {attachments.length > 0 && (
        <div className="composer-attachments">
          {attachments.map((a, i) => (
            <div key={i} className="composer-attachment" title={`${a.name} · ${(a.size / 1024).toFixed(0)} KiB`}>
              <img src={a.dataUrl} alt={a.name} />
              <button
                className="composer-attachment-x"
                onClick={() => removeAt(i)}
                title="Remove"
                type="button"
              >
                <Icon name="x" size={11} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="composer-bar">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            const target = e.currentTarget as HTMLInputElement;
            onPickFiles(target.files);
            target.value = ""; // allow picking the same file twice in a row
          }}
        />
        <button
          className="icon-btn"
          title="Attach image"
          disabled={disabled}
          onClick={() => fileRef.current?.click()}
          type="button"
        >
          <Icon name="attach" />
        </button>
        <textarea
          ref={taRef}
          rows={1}
          value={draft}
          disabled={disabled}
          onInput={(e) => setDraft((e.currentTarget as HTMLTextAreaElement).value)}
          onKeyDown={(e) => {
            // Slash menu takes precedence over send when open — Enter/Tab
            // accepts the highlighted skill, arrows navigate, Esc dismisses
            // (by clearing the draft's slash trigger; the simplest signal
            // is "don't show the menu" so we close by appending a space).
            if (slashOpen) {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setSlashIdx((i) => (i + 1) % slashMatches.length);
                return;
              }
              if (e.key === "ArrowUp") {
                e.preventDefault();
                setSlashIdx((i) => (i - 1 + slashMatches.length) % slashMatches.length);
                return;
              }
              if (e.key === "Enter" || e.key === "Tab") {
                e.preventDefault();
                acceptSlash(slashMatches[slashIdx]);
                return;
              }
              if (e.key === "Escape") {
                e.preventDefault();
                setDraft("");
                return;
              }
            }
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              doSend();
            }
          }}
          placeholder={disabled ? "Thinking…" : "Reply to Vulture Chat…"}
        />
        {mic.available && (
          <button
            className="icon-btn composer-mic"
            data-mic={mic.state}
            style={{ "--mic-level": mic.level } as Record<string, string | number>}
            title={
              mic.state === "streaming" ? "Stop dictation"
              : mic.state === "uploading" ? "Transcribing uploaded audio…"
              : "Dictate"
            }
            disabled={disabled || mic.state === "uploading"}
            onClick={() => mic.toggle()}
            type="button"
          >
            <Icon name="mic" />
          </button>
        )}
        {/* Audio-file upload — sibling to the mic for "I already have a
          * recording, transcribe it." Reuses the same /api/stt endpoint
          * via mic.uploadFile, with a streaming server pass-through so
          * multi-hour files don't sit in the agent's RAM. */}
        <input
          ref={audioFileInputRef}
          type="file"
          accept="audio/*"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = (e.currentTarget as HTMLInputElement).files?.[0];
            if (f) void mic.uploadFile(f);
            (e.currentTarget as HTMLInputElement).value = "";
          }}
        />
        <button
          className="icon-btn composer-audio-upload"
          title={
            mic.state === "uploading" ? "Transcribing uploaded audio…"
            : "Upload an audio file to transcribe"
          }
          disabled={disabled || mic.state !== "idle"}
          onClick={() => audioFileInputRef.current?.click()}
          type="button"
        >
          <Icon name="attach" />
        </button>
        <button
          className="btn btn-primary composer-send"
          onClick={doSend}
          disabled={!canSend}
          style={!canSend ? { opacity: 0.5 } : undefined}
          type="button"
        >
          <Icon name="send" size={14} />
          <span className="desk-only-inline">Send</span>
        </button>
      </div>
    </div>
  );
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error || new Error("read failed"));
    r.onload = () => resolve(String(r.result || ""));
    r.readAsDataURL(file);
  });
}
